import { GraphQLResult } from '@aws-amplify/api-graphql';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { GetHighnotePaymentCardQuery } from 'API';
import { RootState } from 'app/store/rootReducer';
import { graphqlOperation } from 'aws-amplify';
import { GetHighnotePaymentCard } from './queries';
import { graphqlRequest } from 'app/services/middleware';
import { transactionService } from 'app/services/transaction.service';
import type { SearchSpxTransactionsInput } from 'CDK_API';

const initialState = {
  loading: false,
  missingJobCodes: 0,
  missingReceipts: 0,
};

export type priorityActionsState = typeof initialState;

// SEE-1392: the try/catch that used to wrap this swallowed every failure into a
// `fulfilled` with `undefined`, so a denied or failed search was indistinguishable
// from a genuine zero. Letting it reject matches the two sibling thunks here, and
// the rejection is now handled explicitly in the reducer below.
//
// SEE-1393 falls out of the repoint rather than being fixed separately. This read
// `result.data.searchHighnoteTransactions.total`, but the document returns
// `searchSpxTransactions`, so the count was permanently 0. The CDK service resolves
// to the payload directly — there is no envelope and no such key to read — so keeping
// the old behaviour would have meant writing new code whose only purpose was to stay
// wrong. The count is now real, which is a visible change to the insights dashboard:
// this tile has been reading 0 for every paidol and will start showing a number.
export const getMissingJobCodes = createAsyncThunk(
  'cards/prioityActions/getMissingJobCodes',
  async (paidolId: string) => {
    const payload = await transactionService.searchSpxTransactions({
      filter: {
        jobCodeId: { exists: false },
        paidolId: { eq: paidolId },
      },
    } as SearchSpxTransactionsInput);
    return payload?.total || 0;
  }
);

// SEE-301: this tile cannot be computed correctly yet, so it deliberately does not
// query. It never worked: the filter sent `hasReceipt: { ne: true }`, and
// `SpxTransactionsFilterInput` declares no `hasReceipt` in either schema. Amplify
// returned that coercion failure as HTTP 200 data and `|| 0` swallowed it, so the
// tile has read 0 for every paidol. After the repoint `execute` throws, which would
// turn a silent 0 into a rejection on every insights-dashboard mount.
//
// It is NOT fixed by switching to the `receiptPath: ExistsCondition` the input does
// offer. Per SEE-845, `hasReceipt` is the source-of-truth flag the v2-transaction
// Lambda maintains from the `TransactionToReceiptUpload` join table, and
// `receiptPath`/`receiptId` are legacy inline pointers from when a transaction held
// one receipt — the schema says so at HighnoteTransactionEvent.hasReceipt, and
// SEE-845 migrated every reader off `!!receiptPath` onto `hasReceipt`. The join
// table is canonical for the SMS, web, AI-agent and pending→cleared paths, so a
// transaction can carry receipts with no `receiptPath` at all. Filtering on
// `receiptPath` would count those as missing and nag users for receipts they
// already submitted — worse than the 0 the tile shows today.
//
// The real fix is server-side and small: add `hasReceipt: EqualBooleanCondition` to
// `SpxTransactionsFilterInput` (speedchain-cdk, graphql/05-v2-transaction.graphql) and
// map it in the searchSpxTransactions resolver. The field is already in the OpenSearch
// index — the SEE-845 backfill bulk-writes it to `highnotetransactionevent` — so only
// the input and the mapping are missing.
//
// Once that ships, this whole thunk body becomes the query it should always have been
// (the previous body is not sitting below commented out, so here it is in full):
//
//   const MS_PER_DAY = 24 * 60 * 60 * 1000;
//   const twentyFourHoursAgo = new Date(Date.now() - MS_PER_DAY).toISOString();
//   const payload = await transactionService.searchSpxTransactions({
//     filter: {
//       paidolId: { eq: paidolId },
//       approveWithoutReceipts: { ne: true },
//       hasReceipt: { ne: true },
//       eventDate: { lt: twentyFourHoursAgo },
//     },
//   } as SearchSpxTransactionsInput);
//   return payload?.total || 0;
//
// `{ ne: true }` rather than `{ eq: false }` is deliberate: the flag is null on rows
// the SEE-845 backfill never touched, and the FE has always treated null as falsy.
// Pin the filter shape with a test next to jobCodeService.searchShape.test.ts at the
// same time, so it cannot regress to `receiptPath`.
export const getMissingReceipts = createAsyncThunk(
  'cards/priorityActions/getMissingReceipts',
  async (_paidolId: string) => {
    return 0;
  }
);

export const getHighnotePaymentCardDetails = createAsyncThunk(
  'cards/priorityActions/getHighnotePaymentCardDetails',
  async (paymentCardId: string, { dispatch }) => {
    return (
      graphqlRequest(
        graphqlOperation(GetHighnotePaymentCard, {
          paymentCardId,
        }),
        dispatch
      ) as Promise<GraphQLResult<GetHighnotePaymentCardQuery>>
    ).then((result) => result.data?.getHighnotePaymentCard);
  }
);

const priorityActionsSlice = createSlice({
  name: 'cards/priorityActions',
  initialState,
  reducers: {
    resetPriorityActionsSlice: () => initialState,
  },
  extraReducers: (builder) => {
    // These two write unconditionally, with no `if (action.payload)` guard: the payload
    // is a count, and that guard cannot tell a genuine 0 from a missing one.
    //
    // That guard was harmless only while the counts were permanently 0 — the filter
    // defects documented above meant it never fired. Now that they return real numbers,
    // swallowing a 0 means the tile keeps the last non-zero it saw: code all seven
    // flagged transactions, come back to Insights, and it still reads 7. jobCodes.tsx
    // drives the link target, `pointerEvents` and `disabled` off that same value, so the
    // tile stays clickable into a list with nothing left to fix. It would also have been
    // inverted against the .rejected handlers below — a denied count rendering 0 while a
    // true 0 renders stale.
    builder.addCase(getMissingJobCodes.fulfilled, (state, action) => {
      state.missingJobCodes = action.payload;
    });

    builder.addCase(getMissingReceipts.fulfilled, (state, action) => {
      state.missingReceipts = action.payload;
    });

    // SEE-301: the searchSpxTransactions-backed counts can now reject — the CDK
    // dispatcher raises where Amplify returned an errors array alongside HTTP 200.
    // Without these the tile kept whatever number it last held and gave no hint the
    // refresh had failed.
    //
    // Zeroing is NOT a satisfying answer and is known to be temporary: rendering a
    // denied count as "0 need your attention" is a positive claim that the user is
    // done, which is the same lie TransactionsErrorState and flexCodingSearchError
    // exist to stop telling. Stale-and-wrong at least does not claim that. Both
    // readings are wrong in different directions, and picking properly means giving
    // the tile an error affordance — a hadErrorLoading-style flag plus a dash or
    // warning glyph — which is UI work outside this repoint. Deferred to the CDK
    // improvements follow-up; only reachable once these counts return real numbers,
    // which this PR is what makes true.
    builder.addCase(getMissingJobCodes.rejected, (state) => {
      state.missingJobCodes = 0;
    });

    // Unreachable while getMissingReceipts cannot query — kept because it is needed
    // the moment the hasReceipt filter lands, and a count thunk without a rejected
    // case is the omission that made this slice hold stale numbers in the first place.
    builder.addCase(getMissingReceipts.rejected, (state) => {
      state.missingReceipts = 0;
    });
  },
});

export const { resetPriorityActionsSlice } = priorityActionsSlice.actions;

export const selectPriorityActionsSlice = (state: RootState) => state.cards?.priorityActions ?? initialState;

export default priorityActionsSlice.reducer;
